import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    MemberPersonalInfoViewModel,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    LogoComponent,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    MembershipOrganizationComponent,
} from '@sheerid/jslib';

interface StepMemberPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepMemberPersonalInfo = ({ intl, verificationService }: StepMemberPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MemberPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;

    const updateMemberViewModel = (key: keyof MemberPersonalInfoViewModel, value: any) => {
        const nextState: MemberPersonalInfoViewModel = produce(viewModel, (draft: MemberPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };



    return (
        <div id="sid-step-member-personal-info" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-l-horz-center">
                    <LogoComponent verificationService={verificationService} />
                </div>
                <div className="sid-header__title sid-l-horz-center">
                    <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Confirm your eligibility by simply completing the form below for this special employee offer." />
                </div>

                <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="STEP 1: VERIFY YOUR DETAILS" />
                </div>
                <div className="sid-header__all-fields sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.allFields"
                            defaultMessage="All fields are required." />
                </div>
            </div>

            {!viewModel.organization.id ?
                <MembershipOrganizationComponent
                    value={(verificationService.viewModel as MemberPersonalInfoViewModel).organization}
                    verificationService={verificationService}
                    isErrored={!!fieldValidationErrors.organization}
                    onChange={
                        (choice: Organization) => {
                            updateViewModelOrganization(choice, verificationService);
                            updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                        }
                    }
                />
                :null
            }

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
                />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMemberPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            <FormattedHTMLMessage id="verifyMyMembershipStatus" defaultMessage="VERIFY AND CONTINUE" />
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepMemberPersonalInfoComponent = injectIntl(StepMemberPersonalInfo);
